import {
  ActionIcon,
  Anchor,
  Badge,
  Box,
  Button,
  CopyButton,
  Group,
  Indicator,
  Stack,
  Tabs,
  Text,
  TextInput,
  Tooltip,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import dayjs from 'dayjs';
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  PiArchive,
  PiArrowCounterClockwise,
  PiCheck,
  PiCopy,
  PiCopySimple,
  PiEnvelope,
  PiLink,
  PiLockOpen,
} from 'react-icons/pi';
import { Link, Redirect, useLocation, useParams } from 'wouter';
import Loader from '@/components/Loader';
import { StatusHeaderBar } from '@/components/StatusHeaderBar/StatusHeaderBar';
import { useConfirmation } from '@/hooks/useConfirmation';
import { useInvoiceDetailsByOfferId } from '@/models/invoices';
import {
  getOfferStatus,
  offerStatusColor,
  offerStatusLabel,
  TOffer,
  useArchiveOffer,
  useOffer,
  useReSendOffer,
  useRevertOffer,
  useSendOffer,
  useUpdateOffer,
} from '@/models/offers';
import { useOfferTabs } from '@/models/permissions';
import { useSplitPlan } from '@/models/split-plan';
import DuplicateOfferModal from './DuplicateOfferModal';
import classes from './OfferEditorPage.module.css';
import DeliveryTab from './tabs/DeliveryTab';
import FinanceTab from './tabs/FinanceTab';
import GeneralTab from './tabs/GeneralTab';
import LegalTab from './tabs/LegalTab';
import { TestBadge } from '@/components/TestBadge';

const payerKycLabel = (kycExpiresAt: string | null) => {
  if (!kycExpiresAt) return 'KYC missing';
  return new Date(kycExpiresAt) > new Date()
    ? `KYC valid till ${dayjs(kycExpiresAt).format('DD.MM.YYYY')}`
    : `KYC expired ${dayjs(kycExpiresAt).format('DD.MM.YYYY')}`;
};

/**
 * An attached party is not the same as a cleared one. Stating the KYC date as a
 * bare fact reads as "the payer is sorted", so an SR promises the customer a
 * step that has not been cleared. Missing and expired both still require the
 * customer to verify -- bound to this same party, renewing it in place.
 */
const payerNeedsKyc = (kycExpiresAt: string | null) =>
  !kycExpiresAt || new Date(kycExpiresAt) <= new Date();

export const CUSTOMER_PORTAL_URL =
  import.meta.env.VITE_CUSTOMER_PORTAL_URL || 'https://portal.legalbison.com';

export default function OfferHubPage() {
  const { id, tab } = useParams();
  const offerId = Number(id);
  const [, navigate] = useLocation();
  const { data: offer, isLoading, isError } = useOffer(offerId);
  const { data: planData } = useSplitPlan(offerId);
  const { tabs, policy, isLoading: permsLoading } = useOfferTabs();

  const [dupModalOpen, setDupModalOpen] = useState(false);
  const archiveOffer = useArchiveOffer();

  if (isLoading || permsLoading) return <Loader />;
  if (isError || !offer) {
    return (
      <Text c="red">
        Unable to load offer. It may not exist or you don't have permission.
      </Text>
    );
  }

  // A link may carry no tab, an unknown one, or one this viewer may not see. All three
  // resolve to the first tab they are entitled to, so an offer link works for anyone.
  if (!tab || !tabs.some((t) => t.key === tab)) {
    return <Redirect to={`/${offerId}/${tabs[0].key}`} replace />;
  }

  const { status, detail } = getOfferStatus(offer);
  const isAccepted = offer.accepted;
  const offerUrl = `${CUSTOMER_PORTAL_URL}/offer/${offer.uuid}`;
  const planStatus = planData?.plan?.status ?? 'none';
  const awaitingBizDev = planStatus === 'proposed';

  return (
    <Stack gap="md">
      <DuplicateOfferModal
        offerId={offer.id}
        offerTitle={offer.title}
        opened={dupModalOpen}
        onClose={() => setDupModalOpen(false)}
      />

      {/* Header */}
      <StatusHeaderBar
        statusLabel={offerStatusLabel[status]}
        color={offerStatusColor[status]}
        underLayer={
          offer.archivedAt
            ? { label: 'Archived', icon: <PiArchive size={40} /> }
            : undefined
        }
        description={detail}
        title={
          <Group gap="xs" wrap="nowrap">
            <OfferTitleEditor offer={offer} disabled={isAccepted} />
            {offer.isTest && <TestBadge size="md" />}
          </Group>
        }
        subtitles={
          <>
            <Group gap={6} mt={4} wrap="nowrap">
              <Text span fz="sm" fw={500} c="dimmed">
                Offer sends to:
              </Text>
              <Anchor href={`mailto:${offer.contactEmail}`} fz="sm" fw={500}>
                {offer.contactEmail}
              </Anchor>
              <CopyButton value={offer.contactEmail}>
                {({ copied, copy }) => (
                  <Tooltip label={copied ? 'Copied' : 'Copy address'}>
                    <ActionIcon
                      size="xs"
                      variant="subtle"
                      color={copied ? 'teal' : 'gray'}
                      onClick={copy}
                    >
                      {copied ? <PiCheck size={12} /> : <PiCopy size={12} />}
                    </ActionIcon>
                  </Tooltip>
                )}
              </CopyButton>
              {offer.contactName && (
                <Text span fz="sm" c="dimmed">
                  ({offer.contactName})
                </Text>
              )}
            </Group>
            {offer.currentContactEmail &&
              offer.currentContactEmail !== offer.contactEmail && (
                <Text fz="xs" c="orange.7">
                  The contact record now reads {offer.currentContactEmail}. This
                  offer keeps sending to the address captured when it was
                  created.
                </Text>
              )}
            {offer.accountName && (
              <Text fz="sm" c="dimmed">
                <Text span fz="sm" fw={500} c="dimmed">
                  Account:
                </Text>{' '}
                {offer.accountName}
              </Text>
            )}
            {offer.payer && (
              <Text fz="sm" c="dimmed">
                <Text span fz="sm" fw={500} c="dimmed">
                  Payer:
                </Text>{' '}
                <Anchor
                  component={Link}
                  to={`~/payers/${offer.payer.id}`}
                  fz="sm"
                >
                  {offer.payer.displayName}
                </Anchor>
                , {offer.payer.clientType},{' '}
                {payerKycLabel(offer.payer.kycExpiresAt)}
                {offer.payer.deletedAt ? (
                  <Tooltip label="The attached payer was deleted and cannot be used — replace or detach it on the Finance tab">
                    <Badge ml={8} size="xs" color="red" variant="filled">
                      Payer unusable
                    </Badge>
                  </Tooltip>
                ) : (
                  payerNeedsKyc(offer.payer.kycExpiresAt) && (
                    <Tooltip
                      multiline
                      w={300}
                      label="Attaching a payer does not clear verification. The customer will still have to pass KYC before the order can complete — it starts from this party's details and renews it in place."
                    >
                      <Badge ml={8} size="xs" color="orange" variant="filled">
                        Customer must still pass KYC
                      </Badge>
                    </Tooltip>
                  )
                )}
              </Text>
            )}
            <Text fz="sm" c="dimmed">
              <Text span fz="sm" fw={500} c="dimmed">
                Payment terms:
              </Text>{' '}
              {planData?.splitEnabled ? 'Split Payment' : 'Single Invoice'}
            </Text>
            {offer.kycStatus && offer.kycStatus !== 'pending' && (
              <Group gap="xs" mt={6}>
                <KycStatusBadge status={offer.kycStatus} />
              </Group>
            )}
          </>
        }
        actions={
          <>
            {policy.canViewCommercial && (
              <>
                <OfferActions offer={offer} />
                <Button
                  size="xs"
                  variant="light"
                  leftSection={<PiCopySimple size={14} />}
                  onClick={() => setDupModalOpen(true)}
                >
                  Duplicate
                </Button>
                <Button
                  size="xs"
                  variant="light"
                  color="gray"
                  leftSection={
                    offer.archivedAt ? (
                      <PiArrowCounterClockwise size={14} />
                    ) : (
                      <PiArchive size={14} />
                    )
                  }
                  loading={archiveOffer.isPending}
                  onClick={() =>
                    archiveOffer.mutate({
                      offerId: offer.id,
                      archived: !offer.archivedAt,
                    })
                  }
                >
                  {offer.archivedAt ? 'Unarchive' : 'Archive'}
                </Button>
                <CopyButton value={offerUrl}>
                  {({ copied, copy }) => (
                    <Tooltip label={copied ? 'Copied' : 'Copy offer link'}>
                      <Button
                        size="xs"
                        variant="light"
                        leftSection={
                          copied ? <PiCopy size={14} /> : <PiLink size={14} />
                        }
                        onClick={copy}
                        color={copied ? 'teal' : undefined}
                      >
                        {copied ? 'Copied' : 'Copy Link'}
                      </Button>
                    </Tooltip>
                  )}
                </CopyButton>
              </>
            )}
          </>
        }
      />

      {/* Tabs */}
      <Tabs value={tab} onChange={(v) => v && navigate(`/${offerId}/${v}`)}>
        <Tabs.List>
          {tabs.map(({ key, label }) => (
            <Tabs.Tab key={key} value={key}>
              {key === 'finance' ? (
                <Indicator
                  disabled={!awaitingBizDev}
                  color="blue"
                  size={7}
                  offset={-4}
                >
                  <span>{label}</span>
                </Indicator>
              ) : (
                label
              )}
            </Tabs.Tab>
          ))}
        </Tabs.List>

        <Box pt="md">
          {tab === 'general' && <GeneralTab offer={offer} />}
          {tab === 'legal' && <LegalTab offer={offer} />}
          {tab === 'finance' && (
            <FinanceTab offer={offer} restricted={!policy.canViewFullFinance} />
          )}
          {tab === 'delivery' && <DeliveryTab offer={offer} />}
        </Box>
      </Tabs>
    </Stack>
  );
}

const kycBadgeProps: Record<string, { color: string; label: string }> = {
  in_progress: { color: 'yellow', label: 'KYC in progress' },
  approved: { color: 'green', label: 'KYC approved' },
  rejected: { color: 'red', label: 'KYC rejected' },
  retry: { color: 'orange', label: 'KYC retry' },
};

export function KycStatusBadge({ status }: { status: string }) {
  const props = kycBadgeProps[status];
  if (!props) return null;
  return (
    <Badge size="lg" variant="light" color={props.color}>
      {props.label}
    </Badge>
  );
}

function OfferActions({ offer }: { offer: TOffer }) {
  const sendOffer = useSendOffer();
  const reSendOffer = useReSendOffer();
  const { confirm, ConfirmationDialog } = useConfirmation();
  const noAddress = offer.contactEmail?.trim()
    ? null
    : 'This offer has no contact email address, so it cannot be sent';

  const handleSend = async () => {
    const ok = await confirm({
      title: 'Send Offer',
      message: (
        <>
          Send this offer to <b>{offer.contactEmail}</b>?
        </>
      ),
      confirmButtonText: 'Send',
    });
    if (!ok) return;
    try {
      await sendOffer.mutateAsync({ offerId: offer.id });
    } catch (e) {
      notifications.show({
        color: 'red',
        title: 'Error',
        message: `Failed to send: ${e}`,
      });
    }
  };

  const handleReSend = async () => {
    const ok = await confirm({
      title: 'Re-send Offer',
      message: (
        <>
          Re-send this offer to <b>{offer.contactEmail}</b>?
        </>
      ),
      confirmButtonText: 'Re-send',
    });
    if (!ok) return;
    try {
      await reSendOffer.mutateAsync({ offerId: offer.id });
    } catch (e) {
      notifications.show({
        color: 'red',
        title: 'Error',
        message: `Failed to re-send: ${e}`,
      });
    }
  };

  return (
    <>
      <ConfirmationDialog />
      {!offer.accepted && !offer.sentAt && (
        <Tooltip label={noAddress} disabled={!noAddress}>
          <Button
            size="xs"
            leftSection={<PiEnvelope size={14} />}
            onClick={handleSend}
            loading={sendOffer.isPending}
            disabled={Boolean(noAddress)}
          >
            Send
          </Button>
        </Tooltip>
      )}
      {!offer.accepted && offer.sentAt && (
        <Tooltip label={noAddress} disabled={!noAddress}>
          <Button
            size="xs"
            variant="light"
            leftSection={<PiEnvelope size={14} />}
            onClick={handleReSend}
            loading={reSendOffer.isPending}
            disabled={Boolean(noAddress)}
          >
            Re-send
          </Button>
        </Tooltip>
      )}
      {offer.accepted && <RevertButton offer={offer} />}
    </>
  );
}

function OfferTitleEditor({
  offer,
  disabled,
}: {
  offer: TOffer;
  disabled: boolean;
}) {
  const [editing, setEditing] = useState(false);
  const [title, setTitle] = useState(offer.title);
  const update = useUpdateOffer();

  const save = async () => {
    if (title.trim() && title !== offer.title) {
      await update.mutateAsync({ offerId: offer.id, title: title.trim() });
    }
    setEditing(false);
  };

  if (disabled || !editing) {
    return (
      <Text
        fw={600}
        fz="xl"
        onClick={() => !disabled && setEditing(true)}
        style={{ cursor: disabled ? 'default' : 'pointer' }}
      >
        {offer.title}
      </Text>
    );
  }

  return (
    <TextInput
      value={title}
      onChange={(e) => setTitle(e.currentTarget.value)}
      onBlur={save}
      onKeyDown={(e) => e.key === 'Enter' && save()}
      autoFocus
      size="md"
      fw={600}
    />
  );
}

function RevertButton({ offer }: { offer: TOffer }) {
  const { data: invoices } = useInvoiceDetailsByOfferId(offer.id);
  const invoice = invoices?.[0];
  const revert = useRevertOffer();
  const { confirm, ConfirmationDialog } = useConfirmation();
  const [phase, setPhase] = useState<'idle' | 'countdown'>('idle');
  const [seconds, setSeconds] = useState(10);
  const timerRef = useRef<number>(undefined);
  const intervalRef = useRef<number>(undefined);

  const canRevert = invoice?.status === 'issued';

  const cancelCountdown = useCallback(() => {
    clearTimeout(timerRef.current);
    clearInterval(intervalRef.current);
    setPhase('idle');
    setSeconds(10);
  }, []);

  const startCountdown = useCallback(() => {
    setPhase('countdown');
    setSeconds(10);
    intervalRef.current = window.setInterval(
      () => setSeconds((s) => s - 1),
      1000,
    );
    timerRef.current = window.setTimeout(async () => {
      clearInterval(intervalRef.current);
      setPhase('idle');
      try {
        await revert.mutateAsync({ offerId: offer.id });
      } catch (e) {
        notifications.show({
          color: 'red',
          title: 'Error',
          message: `Failed to revert: ${e}`,
        });
      }
    }, 10000);
  }, [offer.id, revert]);

  const handleClick = useCallback(async () => {
    const ok = await confirm({
      title: 'Unlock Offer',
      message: `This will cancel invoice #${invoice?.invoiceNumber} and unlock the offer for editing. This action cannot be undone.`,
      confirmButtonText: 'Unlock',
    });
    if (ok) startCountdown();
  }, [invoice?.invoiceNumber, startCountdown, confirm]);

  useEffect(() => {
    return () => {
      clearTimeout(timerRef.current);
      clearInterval(intervalRef.current);
    };
  }, []);

  if (!canRevert) return null;

  if (phase === 'countdown') {
    return (
      <Button
        size="xs"
        color="gray"
        variant="light"
        onClick={cancelCountdown}
        style={{ position: 'relative', overflow: 'hidden' }}
      >
        <Box className={classes.countdownOverlay} />
        Undo ({seconds}s)
      </Button>
    );
  }

  return (
    <>
      <ConfirmationDialog />
      <Button
        size="xs"
        color="red"
        variant="light"
        leftSection={<PiLockOpen size={14} />}
        onClick={handleClick}
        loading={revert.isPending}
      >
        Unlock Offer
      </Button>
    </>
  );
}
